import React from 'react';
import { selectResources, selectPostFormData } from '../store/selectors';
import { connect } from 'react-redux';
import { CoverageBox, CoverageBoxPills } from 'migration-flos-react';
import { formatPrice, getInsuranceTypes } from '../lib';

const InsuranceProductBox = ({ resources, postForm }) => {
    const { fsguide } = resources;
    const { insurancerequest, customer } = postForm.XmlRequest.request;
    const insuranceTypes = getInsuranceTypes();

    // Filter away insuarnces we don't have implementet
    return Object.entries(insurancerequest)
        .filter((data) => insuranceTypes[data[0]])
        .map((data) => {
            const topResponse = postForm.XmlResponse.response;
            let insuranceType = data[0];
            let insurance = data[1];
            const productResponse = topResponse[insuranceType];
            const { produktpris, produktsamlerabat } = productResponse;
            const pillsArray = Object.keys(productResponse).filter(
                (key) => productResponse[key] === 'true' || productResponse[key] === true
            );
            const pills =
                pillsArray.length > 0 ? (
                    <Pills pills={pillsArray} fsguide={fsguide} insuranceType={insuranceType} />
                ) : null;
            const {
                moreInfo,
                subtitle,
                iObject: { title, icon }
            } = getSubdata(insuranceType, insurance, fsguide, customer, productResponse);
            const coverageTitle = (
                <>
                    {title}
                    <div className="u-Space-down">
                        <small>{moreInfo}</small>
                    </div>
                </>
            );

            return (
                <CoverageBox
                    key={insuranceType}
                    title={coverageTitle}
                    subtitle={subtitle}
                    id={insuranceType}
                    svgIcon={icon}
                    svgIconSize="medium"
                    iconIsResponsive={false}
                    actions={
                        <Actions fsguide={fsguide} produktpris={produktpris} produktsamlerabat={produktsamlerabat} />
                    }
                    pills={pills}
                />
            );
        });
};

/**
 *
 * About price label
 */
const AboutPrice = ({ fsguide }) => (
    <div className="u-Space-up">
        <small>
            <a className="pull-right" rel="noopener noreferrer" target="_blank" href={fsguide.aboutprice.link}>
                {fsguide.aboutprice.label}
            </a>
        </small>
    </div>
);

/**
 * Generate the pills for the coverages
 */
const Pills = ({ pills, fsguide, insuranceType }) =>
    pills.map((pill) => <CoverageBoxPills key={pill}>{fsguide[insuranceType].coverage[pill]}</CoverageBoxPills>);

/**
 *
 * Actions section
 */

const Actions = ({ fsguide, produktpris, produktsamlerabat }) => (
    <div className="text-right">
        <strong>
            {formatPrice(produktpris)} {fsguide.peryear}
        </strong>
        <br />
        {fsguide.collectiblediscount} {formatPrice(produktsamlerabat)} {fsguide.peryear}
        <AboutPrice fsguide={fsguide} />
    </div>
);

/**
 *
 * This section generates the required sub title and more information for each coverages
 */

const getSubdata = (insuranceType, insurance, fsguide, customer, productResponse) => {
    let moreInfo = '';
    let subtitleText = '';
    let iObject = {};
    if (insuranceType === insuranceTypes.bil) {
        const { fabrikat, model, motor, reg_nr } = insurance;
        moreInfo = `${fabrikat} ${model} ${motor} - ${reg_nr}`;
        iObject = insuranceObject(fsguide.bilforsikring, '#bilforsikring');
        subtitleText = (
            <>
                {fsguide.selvrisiko}: {formatPrice(productResponse.selvrisiko)} {fsguide.currency}
                <br />
                {fsguide.bilkmconsumption}: {formatPrice(insurance.koersel_aar)} {fsguide.bilkmperyear}
            </>
        );
    } else if (insuranceType === insuranceTypes.ulykke) {
        moreInfo = getNumberOfPeople(insurance, fsguide);
        iObject = insuranceObject(fsguide.ulykkeforsikring, '#liv-ulykkesforsikring');
        subtitleText = (
            <>
                {fsguide.permenant.injury}: {formatPrice(insurance.forsikringssum_varigt_men)} {fsguide.currency}
                <br /> {fsguide.sum.death}: {formatPrice(productResponse.sum_doed_dig)} {fsguide.currency}
            </>
        );
    } else if (insuranceType === insuranceTypes.indbo) {
        moreInfo = customer.adresse;
        iObject = insuranceObject(fsguide.indboforsikring, '#indboforsikring');
        subtitleText = (
            <>
                {fsguide.frid.indbosum}: {formatPrice(insurance.indbosum)} {fsguide.currency}
                <br />
                {fsguide.selvrisiko}: {formatPrice(productResponse.selvrisiko)} {fsguide.currency}
            </>
        );
    } else if (insuranceType === insuranceTypes.hus) {
        moreInfo = customer.adresse;
        iObject = insuranceObject(fsguide.husforsikring, '#husforsikring');
        subtitleText = (
            <>
                {fsguide.selvrisiko}: {formatPrice(productResponse.selvrisiko)} {fsguide.currency}
            </>
        );
    } else if (insuranceType === insuranceTypes.fritid) {
        moreInfo = customer.adresse;
        iObject = insuranceObject(fsguide.fritidshusforsikring, '#sommerhusforsikring');
        subtitleText = (
            <>
                {fsguide.frid.indbosum}: {formatPrice(insurance.indbosum)} {fsguide.currency}
                <br />
                {fsguide.selvrisiko}: {formatPrice(productResponse.selvrisiko)} {fsguide.currency}
            </>
        );
    } else if (insuranceType === insuranceTypes.aarsrejse) {
        iObject = insuranceObject(fsguide.aarsrejseforsikring, '#rejseforsikring');
        moreInfo =
            +insurance.antal_voksne + +insurance.antal_boern > 1
                ? fsguide.aarsrejse.household
                : fsguide.aarsrejse.single;
    }

    return {
        moreInfo,
        iObject,
        subtitle: <small>{subtitleText}</small>
    };
};

/**
 *
 *
 * To get the number for people for Indbo
 */
const getNumberOfPeople = (insurance, fsguide) => {
    const cohabitantExist = 'samlever' in insurance;
    const kidsExist = 'boern' in insurance;
    const numberOfKids = kidsExist ? insurance.boern.antal_u18 : 0;
    let numberOfPeople = `1 ${fsguide.ulykke.adult}`;
    if (cohabitantExist) {
        numberOfPeople = `2 ${fsguide.ulykke.adults}`;
    }
    if (kidsExist) {
        numberOfPeople = `${numberOfPeople}, ${numberOfKids} ${
            numberOfKids > 1 ? fsguide.ulykke.kids : fsguide.ulykke.kid
        }`;
    }
    return numberOfPeople;
};

const insuranceTypes = getInsuranceTypes();

const insuranceObject = (title, icon) => ({ title, icon });

const mapStates = (state) => ({
    resources: selectResources(state),
    postForm: selectPostFormData(state)
});

export const InsuranceProduct = connect(mapStates, null)(InsuranceProductBox);
